import { ORDER_STATUS_LABELS } from '@/entities/order';
import { addressLookupCopy, type AddressLookupCopy } from '@/shared/ui/address/copy';

/** Het object opzoeken in de BAG: de teksten van elk adres, met een hint en keuzevraag over de keuring. */
const objectAddressCopy: AddressLookupCopy = {
    ...addressLookupCopy,
    hint: 'Het adres, de oppervlakte en het bouwjaar komen uit de BAG zodra postcode en huisnummer er staan.',
    choose: (count) => `Op dit huisnummer staan ${count} adressen. Kies het adres van de keuring.`,
};

/**
 * De ene knop per keuring, overal met dezelfde woorden: op Mijn dag, in de lijst
 * Keuringen en in de kop van één keuring. Eén handeling per keuring, en de zin
 * eronder zegt waarom hij er niet is.
 *
 * "Keuring" is het hele dossier, "opname" het werk op locatie
 * (`docs/woordenlijst.md`). Je start dus een keuring en hervat een opname.
 */
export const surveyActionCopy = {
    start: 'Keuring starten',
    starting: 'Bezig met starten…',
    /** Na het starten: de opname en de vragenlijst komen naar dit apparaat. */
    preparing: 'Opname wordt klaargezet…',
    resume: 'Opname hervatten',
    view: 'Opname bekijken',
    report: 'Rapport openen',
    takeOver: 'Zelf overnemen en starten',
    takingOver: 'Bezig met overnemen…',

    /** De keuring staat op naam van een collega en niemand is begonnen. */
    assignedTo: (name: string) => `Staat op naam van ${name}.`,
    /** Een collega is in de opname; zijn antwoorden staan op zijn apparaat. */
    busyElsewhere: (name: string) => `${name} is met de opname bezig.`,
    /** Als de naam ontbreekt: dan nog liever een mens dan een streepje. */
    aColleague: 'een collega',
    /** Niets te starten: bij deze keuring hoort geen opname, zoals bij een los energielabel. */
    noSurvey: 'Geen opname in de app',

    /** Starten gaat langs de server; hervatten werkt wel gewoon door. */
    offline: 'Starten kan alleen met verbinding.',
    failed: (reason: string) => `Starten lukte niet: ${reason}`,
} as const;

/**
 * "Opname heropenen": in de kop van een keuring en in de detailbalk van Keuringen.
 *
 * Na verzending is de vraag die iemand heeft "wat gebeurt er met wat de klant al
 * heeft?", dus dat staat er in gewone taal bij. Het rapportscherm heeft dezelfde
 * uitleg in zijn paneel "Aanpassen in de opname".
 */
export const reopenSurveyCopy = {
    button: 'Opname heropenen',
    title: 'Opname heropenen',
    /** Het rapport is nog niet verstuurd. */
    explanation:
        'De opname gaat weer open. Het concept-rapport blijft staan; na opnieuw indienen komt er een nieuwe versie.',
    /** Het rapport is verstuurd. */
    explanationSent:
        'De opname gaat weer open. Het verstuurde rapport blijft staan tot je een nieuwe versie vaststelt; daarna ziet de klant die via dezelfde link.',
    nextSteps:
        "Pas de antwoorden, teksten en foto's aan en dien de opname opnieuw in. Daarna staat het concept van de nieuwe versie klaar op het tabblad Rapport.",
    /** Alleen voor wie geld ziet. */
    moneyNote: 'Er gaat geen extra tegoed af en de factuur blijft zoals hij is.',
    reason: 'Reden',
    reasonPlaceholder: 'Bijvoorbeeld: verkeerde foto bij de kozijnen.',
    reasonHint: 'Komt op de tijdlijn van de keuring, met je naam.',
    confirm: 'Heropenen en naar de opname',
    busy: 'Bezig met heropenen',
    preparing: 'Opname wordt klaargezet…',
    cancel: 'Annuleren',
    close: 'Sluiten',
    offline: 'Heropenen kan alleen met verbinding.',
    cancelled: 'Deze keuring is geannuleerd. De opname gaat niet meer open.',
    failed: 'Heropenen lukte niet. Probeer het opnieuw.',
} as const;

/**
 * Nederlandse teksten voor Mijn dag (scherm 3) en Keuringen (scherm 5).
 * Jij-vorm, labels zijn zelfstandige naamwoorden, knoppen werkwoorden.
 */
export const myDayCopy = {
    title: 'Mijn dag',
    eyebrow: (day: string, stops: number, km: number) =>
        `${day} · ${stops} ${stops === 1 ? 'stop' : 'stops'} · ${km} km`,
    startNext: 'Volgende opname starten',

    stopResume: surveyActionCopy.resume,
    stopStart: surveyActionCopy.start,
    /* Opent de keuring op haar tabblad Overzicht; de knop zegt wat hij doet. */
    stopOpenOrder: 'Keuring openen',
    stopDuration: (minutes: number) => `${minutes} min`,
    stopDistance: (km: number) => `${km} km`,
    stopsEmpty: 'Geen stops vandaag. Je agenda is leeg.',

    offlineTitle: 'Offline klaargezet',
    /**
     * Wat er echt op dit apparaat staat: geen belofte, maar de toestand van nu. `total` en
     * `ready` tellen de keuringen met een opname; `withoutSurvey` de afspraken zonder.
     */
    offlineBody: (state: {
        total: number;
        ready: number;
        withoutSurvey: number;
        changes: number;
        photos: number;
        synced: boolean;
    }) => {
        const stock =
            state.total === 0
                ? state.withoutSurvey > 0
                    ? 'Er hoeft niets op dit apparaat klaar te staan: bij je keuringen hoort geen opname.'
                    : state.synced
                      ? 'Er staan nu geen keuringen voor je klaar.'
                      : 'Er staat nog niets op dit apparaat. Zodra er verbinding is, komen je keuringen binnen.'
                : state.ready === state.total
                  ? `${state.total === 1 ? 'De keuring staat' : `Alle ${state.total} keuringen staan`} met ${state.total === 1 ? 'de' : 'hun'} vragenlijst op dit apparaat, ook zonder verbinding.`
                  : state.total === 1
                    ? 'De keuring staat nog niet klaar. De vragenlijst komt binnen zodra er verbinding is.'
                    : `${state.ready} van ${state.total} keuringen ${state.ready === 1 ? 'staat' : 'staan'} klaar. De vragenlijst van de rest komt binnen zodra er verbinding is.`;
        const waiting = [
            state.changes > 0
                ? `${state.changes} ${state.changes === 1 ? 'wijziging' : 'wijzigingen'}`
                : null,
            state.photos > 0 ? `${state.photos} ${state.photos === 1 ? 'foto' : "foto's"}` : null,
        ].filter(Boolean);

        return waiting.length === 0
            ? stock
            : `${stock} ${waiting.join(' en ')} ${waiting.length === 1 && (state.changes === 1 || state.photos === 1) ? 'wacht' : 'wachten'} op verbinding.`;
    },
    offlineReady: (ready: number, total: number, size: string) =>
        `${ready}/${total} gereed · ${size}`,

    errorTitle: 'Je dag kon niet geladen worden',

    /* Mijn dag uit de lokale opslag. */
    eyebrowLocal: (day: string, stops: number) =>
        `${day} · ${stops} ${stops === 1 ? 'stop' : 'stops'}`,
    upcomingTitle: 'Komende keuringen',
    stopTime: (time: string, date: string | null) => (date ? `${date} ${time}` : time),
    stopUnscheduled: 'Nog niet ingepland',
    stopNoAddress: 'Adres onbekend',
    stopNotOnDevice: 'Nog niet op dit apparaat',
    /** Een afspraak zonder opname, zoals een energielabel zonder vragenlijst: niets te starten. */
    stopNoSurvey: surveyActionCopy.noSurvey,
    stopTimeUnknown: '—',
    loading: 'Keuringen laden',
    noEnvironmentTitle: 'Nog geen omgeving gekozen',
    noEnvironmentBody:
        'Log in of kies het bedrijf waarvoor je vandaag werkt. Daarna staan je keuringen op dit apparaat.',
    impersonatingTitle: 'Mijn dag is niet beschikbaar tijdens het meekijken',
    impersonatingBody:
        'Mijn dag toont de keuringen die op het apparaat van de inspecteur zelf staan, en tijdens het meekijken komt daar niets van op dit apparaat. Keuringen en rapporten bekijk je via Keuringen.',
    toInspections: 'Naar Keuringen',
    notBootstrapped:
        'Je keuringen komen binnen zodra er verbinding is. Daarna werkt Mijn dag ook zonder verbinding.',
    stopView: surveyActionCopy.view,
    stopSubmitted: 'Ingediend',
} as const;

export const inspectionsCopy = {
    title: 'Keuringen',
    eyebrow: (total: number) => `${total} ${total === 1 ? 'keuring' : 'keuringen'}`,
    /**
     * De regel onder de kop: wat nu op jou wacht, per status, en elk deel zet
     * het filter op die status. Geen optelsom "vragen aandacht": daar bestaat
     * geen filter voor, en twee soorten werk tellen niet op tot één getal.
     */
    attentionLabel: 'Wacht op jou',
    attentionLead: 'Wacht op jou:',
    attentionNew: (n: number) => `${n} nieuw`,
    attentionDraft: (n: number) =>
        n === 1 ? '1 rapport na te lezen' : `${n} rapporten na te lezen`,
    eyebrowLoading: 'Keuringen laden',
    export: 'Exporteren',
    exporting: 'Exporteren…',
    exportFailed: 'De export is niet gelukt',
    exportFileName: (date: string) => `keuringen-${date}.csv`,
    newInspection: 'Nieuwe keuring',

    searchPlaceholder: 'Zoek adres, klant of nummer',
    searchLabel: 'Zoek in keuringen',
    serviceLabel: 'Filter op dienst',
    serviceAll: 'Alle diensten',
    sortLabel: 'Sorteervolgorde',
    sortPrefix: 'Sorteer',
    filterGroupLabel: 'Filter op status',

    tableLabel: 'Keuringen',
    columnId: 'Nummer',
    columnStatus: 'Status',
    columnDate: 'Opname',
    columnAddress: 'Adres',
    columnService: 'Dienst',
    columnInspector: 'Inspecteur',
    columnPrice: 'Prijs',
    /** De ene handeling van die keuring, zonder hem eerst te hoeven openen. */
    columnAction: 'Actie',
    noDate: '—',
    noInspector: 'niet toegewezen',
    empty: 'Geen keuringen met dit filter.',
    /* Zonder filter en zoekterm: het bedrijf heeft er nog geen. */
    emptyAll: 'Nog geen keuringen. Maak de eerste met Nieuwe keuring.',
    emptyAllReadOnly: 'Nog geen keuringen.',
    emptySearch: (term: string) => `Geen keuringen gevonden voor "${term}".`,
    pagerNoun: 'keuringen',

    /* Export */
    csvHeaders: {
        number: 'Nummer',
        status: 'Status',
        scheduledAt: 'Opname',
        address: 'Adres',
        postcode: 'Postcode',
        city: 'Plaats',
        service: 'Dienst',
        inspector: 'Inspecteur',
        client: 'Klant',
        price: 'Prijs excl. btw',
    },

    /* Detailbalk */
    detailLabel: 'Details van de geselecteerde keuring',
    /* Opent de keuring op haar tabblad Overzicht. */
    detailOpenOrder: 'Keuring openen',
    /* Springt naar het blok Documenten op dat tabblad, en heet daarom ook zo. */
    detailFiles: 'Documenten',
    detailClientPage: 'Klantpagina',
    detailCollapse: 'Inklappen',
    detailExpand: 'Uitklappen',
    detailClose: 'Sluiten',
    contactEyebrow: 'Contact & verzonden',
    lifecycleEyebrow: 'Levenscyclus van deze keuring',
    sentConfirmation: 'bevestiging',
    sentAppointment: 'afspraak',
    sentReport: 'rapport',
    sentInvoice: 'factuur',
    sentYes: '✓',
    sentNo: '—',

    /* Vervolgacties */
    actionSchedule: 'Afspraak maken',
    actionReschedule: 'Afspraak verplaatsen',
    actionConfirm: 'Keuring bevestigen',
    actionOpenSurvey: 'Opname openen',
    actionReviewReport: 'Rapport nalezen',
    actionInvoice: 'Factuur maken',
    actionInvoices: 'Naar facturatie',
    actionCancel: 'Annuleren',
    stepTo: (label: string) => `Zet op ${label}`,
    /**
     * Wat een statusstap doet. Alleen bevestigen gaat naar de klant (`order_confirmed`);
     * inplannen en annuleren hebben een eigen formulier met een eigen zin.
     */
    stepConfirm: (number: string, label: string) =>
        `Keuring ${number} op ${label} zetten? ${
            label === ORDER_STATUS_LABELS.confirmed
                ? 'De stap komt in de tijdlijn, en de klant krijgt per mail een bevestiging als er een e-mailadres bekend is.'
                : 'De stap komt in de tijdlijn; de klant krijgt er geen bericht over.'
        }`,
    stepYes: (label: string) => `Ja, zet op ${label}`,
    stepNo: 'Niet doen',
    stepDone: (number: string, label: string) => `Keuring ${number} staat nu op ${label}.`,

    cancelTitle: (number: string) => `Keuring ${number} annuleren`,
    cancelBody:
        'De keuring vervalt en het gereserveerde tegoed komt terug. De reden komt alleen in de tijdlijn. De klant krijgt per mail bericht dat de keuring is geannuleerd, als er een e-mailadres bekend is, en per sms als er nog een afspraak stond.',
    cancelReason: 'Reden',
    cancelReasonPlaceholder: 'Bijvoorbeeld: koop gaat niet door',
    cancelReasonTooShort: 'Geef een reden van minimaal 3 tekens.',
    cancelYes: 'Ja, keuring annuleren',
    cancelNo: 'Terug',
    cancelledNote: (reason: string | null) =>
        reason ? `Geannuleerd: ${reason}` : 'Deze keuring is geannuleerd.',

    invoiceCreated: (number: string) => `Er staat een conceptfactuur klaar voor keuring ${number}.`,
    invoiceExists: 'Voor deze keuring staat al een factuur.',
    toBilling: 'Naar facturatie',

    /* Problemen bij schrijven */
    problemTransitionNow: (current: string | null) =>
        current
            ? `Deze stap kan niet meer: de keuring staat inmiddels op ${current}.`
            : 'Deze stap kan niet meer vanaf de huidige status.',
    problemTransitionNext: (allowed: string[]) =>
        allowed.length > 0
            ? ` Wat nu kan: ${allowed.join(', ')}.`
            : ' Er is vanaf hier geen stap meer mogelijk.',
    /**
     * Te weinig keuringen voor een nieuwe keuring. Met een abonnement zijn de
     * keuringen van deze maand op: extra keuringen nu, een groter abonnement vanaf
     * de volgende maand. Zonder abonnement, en in de proef, een abonnement of een
     * losse rapportage. Dezelfde woorden als de 402 van de API.
     */
    problemTrialUsed:
        'Je gratis keuringen zijn gebruikt. Kies een abonnement of koop een losse rapportage om nieuwe keuringen te maken; alles wat je al deed, blijft gewoon staan.',
    problemMonthUsed: (required: number | null, available: number | null) =>
        required !== null && available !== null && available > 0
            ? `Hiervoor zijn ${required} keuringen nodig en je hebt er nog ${available} deze maand. Koop extra keuringen om deze keuring toch te maken, of kies een groter abonnement vanaf de volgende maand.`
            : 'De keuringen van deze maand zijn op. Koop extra keuringen om nu door te gaan, of kies een groter abonnement vanaf de volgende maand.',
    problemCredits: (required: number | null, available: number | null, trial = false) =>
        required !== null && available !== null && available > 0
            ? `Hiervoor zijn ${required} keuringen nodig en je hebt er nog ${available}${trial ? ' gratis' : ''}. Kies een abonnement of koop een losse rapportage om deze keuring toch te maken.`
            : 'Je keuringen zijn op. Kies een abonnement of koop een losse rapportage om nieuwe keuringen te maken.',
    choosePlan: 'Kies een abonnement',
    buySingleReport: 'Losse rapportage kopen',
    buyExtraInspections: 'Extra keuringen kopen',
    chooseBiggerPlan: 'Groter abonnement kiezen',

    /* Afspraak */
    scheduleTitle: 'Afspraak maken',
    scheduleIntro: (address: string) =>
        `Wie gaat er naar ${address}, en wanneer? De klant krijgt de afspraak per mail, en per sms als er een telefoonnummer bekend is.`,
    scheduleInspector: 'Inspecteur',
    scheduleDate: 'Datum',
    scheduleTime: 'Starttijd',
    scheduleDuration: 'Duur',
    durationOption: (minutes: number) => {
        const rest = minutes % 60;
        const restLabel = `${rest} ${rest === 1 ? 'minuut' : 'minuten'}`;

        return minutes < 60
            ? restLabel
            : rest === 0
              ? `${minutes / 60} uur`
              : `${Math.floor(minutes / 60)} uur ${restLabel}`;
    },
    scheduleSave: 'Afspraak opslaan',
    scheduleCancel: 'Annuleren',
    scheduleMissing: 'Kies een inspecteur, een datum en een tijd.',
    scheduleDone: (number: string, when: string) => `Keuring ${number} staat ingepland op ${when}.`,

    /* Nieuwe keuring */
    newTitle: 'Nieuwe keuring',
    newIntro: 'Klant, object en dienst. De afspraak plan je meteen of later.',
    newClose: 'Sluiten',
    newClientSection: 'Klant',
    newClientExisting: 'Bestaande klant',
    newClientNew: 'Nieuwe klant',
    newClientSearch: 'Zoek klant',
    newClientSearchPlaceholder: 'Naam van persoon of bedrijf',
    newClientSearching: 'Zoeken…',
    newClientNone: 'Geen klant gevonden. Maak een nieuwe aan.',
    newClientResults: 'Gevonden klanten',
    newClientChange: 'Andere kiezen',
    newClientType: 'Soort',
    newClientName: 'Naam',
    newClientContact: 'Contactpersoon',
    newClientEmail: 'E-mailadres',
    newClientPhone: 'Telefoon',
    /** Een particulier woont ergens; een bedrijf of makelaar heeft gewoon een adres. */
    newClientHomeSection: (type: string) => (type === 'private' ? 'Huidig woonadres' : 'Adres'),
    newClientHomeSameAsObject: 'Zelfde als keuringsadres',
    newClientHomeSameAsObjectHint: 'Voor een verkoper, of een eigenaar die zelf in het pand woont.',
    newClientHomeFromObject: (address: string | null) =>
        address
            ? `Het adres van het object: ${address}.`
            : 'Het adres van het object hieronder wordt ook het adres van de klant.',
    newClientHomeChange: 'Adres wijzigen',
    newPropertySection: 'Object',
    objectAddress: objectAddressCopy,
    newAddressKnownEyebrow: 'Al in je keuringen',
    newLookupKnown: 'Dit adres staat al in je keuringen. De nieuwe keuring komt erbij.',
    newAddressFloorArea: 'Oppervlakte',
    newAddressBuildYear: 'Bouwjaar',
    newAddressUsage: 'Gebruiksdoel',
    newAddressUnknown: '—',
    newAddressIncomplete: 'Oppervlakte, bouwjaar en gebruiksdoel konden nu niet worden opgehaald.',
    newServiceSection: 'Dienst',
    newService: 'Dienst',
    newServicePick: 'Kies een dienst',
    /** De bouwtechnische keuring met de NHG-bijlage: één keuze, twee diensten. */
    newServiceTogetherHint: 'Twee diensten, twee regels op de factuur.',
    /** Idem, maar je mag de tarieven zien: dan zeggen we wat de bijlage kost. */
    newServicePricedHint: (price: string) =>
        `Twee regels op de factuur: de keuring en ${price} voor de bijlage.`,
    /** Geen tarief voor de bijlage: dan hoort hij bij de prijs van de keuring. */
    newServiceIncludedHint: (appendix: string) =>
        `De ${appendix} is inbegrepen bij de prijs van de keuring.`,
    /** De keuring zonder de bijlage, achter "Andere samenstelling". */
    newServiceWithoutAppendix: (appendix: string) => `Zonder ${appendix}.`,
    /** De bijlage zonder de keuring, achter "Andere samenstelling". */
    newServiceAppendixOnly: 'Alleen de bijlage, zonder opname.',
    newServiceSeparate: 'Andere samenstelling',
    newServiceSeparateHint: 'Je kunt de keuring en de bijlage nu ook los kiezen.',
    newScheduleNow: 'Meteen inplannen',
    newScheduleNowHint: 'Kies nu de inspecteur en het moment, of plan de afspraak later.',
    newNotes: 'Opmerking',
    newNotesPlaceholder: 'Bijvoorbeeld: sleutel ophalen bij de makelaar',
    newSubmit: 'Keuring aanmaken',
    newSubmitting: 'Aanmaken…',
    newCancel: 'Annuleren',
    newMissingClient: 'Kies een klant of maak een nieuwe aan.',
    newMissingClientHome: 'Maak het adres van de klant af, of maak de velden leeg.',
    newMissingProperty:
        'Vul postcode en huisnummer in en kies het adres, of vul straat en plaats zelf in.',
    newMissingService: 'Kies een dienst.',
    newCreated: (number: string) => `Keuring ${number} is aangemaakt.`,

    errorTitle: 'De werklijst kon niet geladen worden',
    actionFailed: 'Dat is niet gelukt',
} as const;
